import React, { useState } from 'react';
import {
  makeStyles,
  Tab,
  TabList,
  Button,
  tokens,
  Tooltip
} from '@fluentui/react-components';
import type { SelectTabEvent, SelectTabData } from '@fluentui/react-components';
import {
  List24Regular,
  TopSpeed24Regular,
  CalendarClock24Regular,
  ArrowClockwise24Regular,
  Settings24Regular
} from '@fluentui/react-icons';
import { DlqList } from '../../components/DLQ/DlqList';
import { DlqDashboardOptimized } from '../../components/DLQ/DlqDashboardOptimized';
import { ScheduledMessagesList } from '../../components/DLQ/ScheduledMessagesList';
import { RetryConfigModal } from '../../components/DLQ/RetryConfigModal';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  tabsContainer: {
    flex: 1,
  },
  configButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  configLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginRight: '4px',
  },
  tabContent: {
    marginTop: '16px',
  },
  tabLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  badge: {
    marginLeft: '4px',
  },
});

type TabValue = 'dashboard' | 'messages' | 'scheduled';

export const DlqPage: React.FC = () => {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState<TabValue>('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);
  const [retryModalOpen, setRetryModalOpen] = useState(false);

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleConfigSaved = () => {
    // Pode adicionar logica adicional aqui se necessario
    // Por exemplo, mostrar uma notificacao ou atualizar dados
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.tabsContainer}>
          <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
            <Tab value="dashboard" icon={<TopSpeed24Regular />}>
              Dashboard
            </Tab>
            <Tab value="messages" icon={<List24Regular />}>
              Mensagens DLQ
            </Tab>
            <Tab value="scheduled" icon={<CalendarClock24Regular />}>
              Agendadas
            </Tab>
          </TabList>
        </div>

        <div className={styles.configButtons}>
          <span className={styles.configLabel}>
            <Settings24Regular style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            Configuracoes:
          </span>
          <Tooltip content="Configurar intervalo e quantidade de tentativas de retry por tipo de mensagem" relationship="label">
            <Button
              appearance="subtle"
              icon={<ArrowClockwise24Regular />}
              onClick={() => setRetryModalOpen(true)}
            >
              Retry
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className={styles.tabContent}>
        {selectedTab === 'dashboard' && (
          <DlqDashboardOptimized key={`dashboard-${refreshKey}`} onRefresh={handleRefresh} />
        )}
        {selectedTab === 'messages' && (
          <DlqList key={`messages-${refreshKey}`} />
        )}
        {selectedTab === 'scheduled' && (
          <ScheduledMessagesList key={`scheduled-${refreshKey}`} />
        )}
      </div>

      {/* Modal de Configuracao de Retry */}
      <RetryConfigModal
        open={retryModalOpen}
        onClose={() => setRetryModalOpen(false)}
        onSaved={handleConfigSaved}
      />
    </div>
  );
};

export default DlqPage;
